import { object, string } from 'yup';

import { appInfo, i18nt } from '../../../../../../../../../root';
import { validatePhoneNumber } from '../../../../../../../../../services/geo/e-164';
import { getPostalCodeRegex } from '../../../../../../../../../services/geo/index';
import { NSPP_PATIENT } from '../../../../../../../../../services/i18n/namespaces';
import { PAYMENT_TYPE_DIGITAL } from '../../../../../constants';
import { isValidSsn } from '../../FinanceDetails/financeDetailsService';

export const ssnSchema = string()
  .nullable()
  .when(['$financialFieldsRequired', 'paymentType', 'ssnOptional', '$isEdit', '$existingSsn'], function(
    [financialFieldsRequired, paymentType, ssnOptional, isEdit, existingSsn],
    schema,
    { value }
  ) {
    let nextSchema = schema;
    const optionalSsnEnabled = appInfo?.features?.optionalSsnEnabled;
    const globalAddressesEnabled = appInfo?.features?.globalAddressesEnabled;
    const isSsnOptional = !!optionalSsnEnabled && paymentType === PAYMENT_TYPE_DIGITAL && ssnOptional;
    if (financialFieldsRequired && !isSsnOptional && !globalAddressesEnabled) {
      nextSchema = nextSchema.required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT }));
    }
    if (value) {
      nextSchema = nextSchema.when(['address.country'], function([country], schema, { value }) {
        if (country === 'US') {
          return schema.test('valid-ssn', i18nt('validation.invalidSsn', { ns: NSPP_PATIENT }), () =>
            isValidSsn(value)
          );
        }
        return schema.length(11, i18nt('validation.invalidSin', { ns: NSPP_PATIENT }));
      });
      if (isEdit) {
        nextSchema = nextSchema.when(['address.country'], function([country], schema, { value }) {
          const message = i18nt(country === 'US' ? 'validation.ssnNotFullyUpdated' : 'validation.sinNotFullyUpdated', {
            ns: NSPP_PATIENT
          });
          return schema.test(
            'ssn-fully-updated',
            message,
            () => value === existingSsn || (value.length === 11 && !value.includes('x'))
          );
        });
      }
    }
    return nextSchema;
  });

export const phoneNumberSchema = object()
  .nullable()
  .test('is-valid-phone-number', function(value) {
    const error = validatePhoneNumber(value.countryId, value.nationalNumber);
    if (error) {
      return this.createError({ message: error });
    }
    return true;
  });

export const phonesInfoSchema = object({
  primary: phoneNumberSchema,
  alternative: phoneNumberSchema
}).when(['$financialFieldsRequired'], function([financialFieldsRequired], schema) {
  if (financialFieldsRequired) {
    return schema.concat(
      object({
        primary: phoneNumberSchema
          .test(
            'required',
            i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT }),
            value => !!value?.nationalNumber
          )
          .required()
      })
    );
  }
  return schema;
});

const postalCodeSchema = string()
  .nullable()
  .when(['country'], function([country], schema, { value }) {
    if (!value) {
      return schema;
    }
    const postalCodeRegex = getPostalCodeRegex(country);
    const postalCodeValidationMessageKey =
      country === 'US' ? 'validation.zipCodeFillCorrectly' : 'validation.postalCodeFillCorrectly';
    return schema.matches(postalCodeRegex, i18nt(postalCodeValidationMessageKey, { ns: NSPP_PATIENT }));
  });

export const addressSchema = object({
  zipCode: postalCodeSchema,
  country: string().required(i18nt('validation.required', { ns: NSPP_PATIENT }))
}).when(['$financialFieldsRequired'], function([financialFieldsRequired], schema) {
  if (financialFieldsRequired) {
    return schema.concat(
      object({
        zipCode: postalCodeSchema.required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT })),
        addressLine1: string().required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT })),
        city: string().required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT })),
        state: string().required(i18nt('validation.requiredForStipendCard', { ns: NSPP_PATIENT }))
      })
    );
  }
  return schema;
});
